import { useLayoutEffect, useRef, useState } from "react";

import { containedMediaRect, projectNormalizedBox } from "./framingOverlayMath";
import type { SceneFramingOverlay } from "./manifestFraming";

// ---------------------------------------------------------------------------
// CropOverlay — draws the framing decision's crop window (and the applied
// receipt's window, when it differs) over the ORIGINAL photo in the Director View
// sync viewer (QCSV-008). Letterbox-aware: the panel renders the original
// `object-contain`, so normalized boxes are projected into the contained
// media rect, not the element box. Geometry reuses `framingOverlayMath` —
// the same math the Framing Workbench pixel view uses.
// ---------------------------------------------------------------------------

export function CropOverlay({
  framing,
  naturalWidth,
  naturalHeight,
}: {
  framing: SceneFramingOverlay;
  /** Measured dimensions of the displayed original (fallback for source dims). */
  naturalWidth: number | null;
  naturalHeight: number | null;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [hostBox, setHostBox] = useState<{
    width: number;
    height: number;
  } | null>(null);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const measure = () => {
      const rect = host.getBoundingClientRect();
      setHostBox((previous) =>
        previous &&
        Math.abs(previous.width - rect.width) < 0.5 &&
        Math.abs(previous.height - rect.height) < 0.5
          ? previous
          : { width: rect.width, height: rect.height },
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  const mediaWidth = framing.sourceWidth ?? naturalWidth;
  const mediaHeight = framing.sourceHeight ?? naturalHeight;
  const ready =
    hostBox != null &&
    mediaWidth != null &&
    mediaHeight != null &&
    mediaWidth > 0 &&
    mediaHeight > 0;

  const mediaRect = ready
    ? containedMediaRect(hostBox.width, hostBox.height, mediaWidth, mediaHeight)
    : null;
  const decisionRect =
    mediaRect && framing.cropWindow
      ? projectNormalizedBox(framing.cropWindow, mediaRect)
      : null;
  const appliedRect =
    mediaRect && framing.appliedCropWindow
      ? projectNormalizedBox(framing.appliedCropWindow, mediaRect)
      : null;

  return (
    <div
      ref={hostRef}
      data-testid="crop-overlay"
      className="pointer-events-none absolute inset-0"
    >
      {decisionRect ? (
        <div
          data-testid="crop-overlay-decision"
          className="absolute border-2 border-emerald-400 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]"
          style={{
            left: decisionRect.x,
            top: decisionRect.y,
            width: decisionRect.width,
            height: decisionRect.height,
          }}
        >
          <span className="absolute -top-5 left-0 rounded bg-emerald-400 px-1 text-[10px] font-medium text-black">
            kept
          </span>
        </div>
      ) : null}
      {appliedRect &&
      (decisionRect == null ||
        Math.abs(appliedRect.x - decisionRect.x) > 1 ||
        Math.abs(appliedRect.y - decisionRect.y) > 1 ||
        Math.abs(appliedRect.width - decisionRect.width) > 1 ||
        Math.abs(appliedRect.height - decisionRect.height) > 1) ? (
        <div
          data-testid="crop-overlay-applied"
          className="absolute border-2 border-dashed border-sky-400"
          style={{
            left: appliedRect.x,
            top: appliedRect.y,
            width: appliedRect.width,
            height: appliedRect.height,
          }}
        >
          <span className="absolute -bottom-5 left-0 rounded bg-sky-400 px-1 text-[10px] font-medium text-black">
            applied
          </span>
        </div>
      ) : null}
    </div>
  );
}
